import { type ControlCode, ControlCodes } from '@bcube/shared/constants';

export type ControlNodeKind = 'module' | 'menu' | 'submenu' | 'permission';

export interface ControlNode {
  code: ControlCode;
  parentCode?: ControlCode;
  name: string;
  description: string;
  isToggle: boolean;
  kind: ControlNodeKind;
  children?: ControlNode[];
}

const group = (
  code: ControlCode,
  name: string,
  kind: Exclude<ControlNodeKind, 'permission'>,
  children: ControlNode[],
  description = name,
): ControlNode => ({
  code,
  name,
  description,
  isToggle: false,
  kind,
  children: children.map((child) => ({
    ...child,
    parentCode: child.parentCode ?? code,
  })),
});

const permission = (
  code: ControlCode,
  name: string,
  description: string,
  children?: ControlNode[],
  parentCode?: ControlCode,
): ControlNode => ({
  code,
  parentCode,
  name,
  description,
  isToggle: true,
  kind: 'permission',
  children: children?.map((child) => ({
    ...child,
    parentCode: child.parentCode ?? code,
  })),
});

const view = (code: ControlCode, description: string, children?: ControlNode[]) =>
  permission(code, 'Visualizar', description, children);

const controlsTree: ControlNode[] = [
  group(ControlCodes.admin, 'Admin', 'module', [
    group(ControlCodes.admin_control, 'Controle de acesso', 'menu', [
      view(
        ControlCodes.admin_control_view,
        'Permite consultar os controles e os perfis vinculados.',
        [
          permission(
            ControlCodes.admin_control_update,
            'Perfil',
            'Permite selecionar um perfil e adicionar ou remover seus acessos.',
          ),
        ],
      ),
    ]),
    group(ControlCodes.admin_profile, 'Perfis', 'menu', [
      view(
        ControlCodes.admin_profile_view,
        'Permite consultar perfis, aplicar filtros e visualizar os resultados.',
        [
          permission(
            ControlCodes.admin_profile_create,
            'Novo perfil',
            'Permite acessar o botão e a modal de cadastro de perfis.',
          ),
          permission(
            ControlCodes.admin_profile_inactive,
            'Ativar/Inativar',
            'Permite ativar ou inativar perfis.',
          ),
        ],
      ),
    ]),
    group(ControlCodes.admin_user, 'Usuários', 'menu', [
      view(
        ControlCodes.admin_user_view,
        'Permite consultar usuários, aplicar filtros e visualizar os resultados.',
        [
          permission(
            ControlCodes.admin_user_create,
            'Novo Usuário',
            'Permite acessar o botão e a tela de cadastro de novos usuários.',
          ),
          permission(
            ControlCodes.admin_user_update,
            'Editar',
            'Permite editar, bloquear, redefinir senha, ativar e inativar usuários.',
          ),
        ],
      ),
    ]),
  ]),

  group(ControlCodes.production, 'Produção', 'module', [
    group(ControlCodes.production_device, 'Dispositivos', 'menu', [
      view(ControlCodes.production_device_view, "Permite acessar a tela 'Dispositivos'."),
    ]),
    group(ControlCodes.production_tool, 'Ferramentas', 'menu', [
      group(ControlCodes.production_tool_auto_test, 'Auto Teste', 'submenu', [
        view(
          ControlCodes.production_tool_auto_test_view,
          "Permite acessar a tela 'Auto Teste' em Ferramentas.",
        ),
      ]),
      group(ControlCodes.production_tool_configuration_check, 'Check de Configuração', 'submenu', [
        view(
          ControlCodes.production_tool_configuration_check_view,
          "Permite acessar a tela 'Check de Configuração' em Ferramentas.",
        ),
      ]),
      group(ControlCodes.production_tool_configurator, 'Configurador', 'submenu', [
        view(
          ControlCodes.production_tool_configurator_view,
          "Permite acessar a tela 'Configurador' em Ferramentas.",
        ),
      ]),
      group(ControlCodes.production_tool_firmware_recording, 'Gravação de Firmware', 'submenu', [
        view(
          ControlCodes.production_tool_firmware_recording_view,
          "Permite acessar a tela 'Gravação de Firmware' em Ferramentas.",
        ),
      ]),
      group(ControlCodes.production_tool_identifier, 'Identificador', 'submenu', [
        view(
          ControlCodes.production_tool_identifier_view,
          "Permite acessar a tela 'Identificador' em Ferramentas.",
        ),
      ]),
      group(ControlCodes.production_tool_serial_terminal, 'Terminal Serial', 'submenu', [
        view(
          ControlCodes.production_tool_serial_terminal_view,
          "Permite acessar a tela 'Terminal Serial' em Ferramentas.",
        ),
      ]),
    ]),
    group(ControlCodes.production_logs, 'Logs', 'menu', [
      group(ControlCodes.production_logs_auto_test, 'Auto Teste', 'submenu', [
        view(
          ControlCodes.production_logs_auto_test_view,
          "Permite acessar a tela 'Auto Teste' em Logs.",
        ),
      ]),
      group(ControlCodes.production_logs_configurator, 'Configuração', 'submenu', [
        view(
          ControlCodes.production_logs_configurator_view,
          "Permite acessar a tela 'Configuração' em Logs.",
        ),
      ]),
      group(ControlCodes.production_logs_firmware_recording, 'Gravação de Firmware', 'submenu', [
        view(
          ControlCodes.production_logs_firmware_recording_view,
          "Permite acessar a tela 'Gravação de Firmware' em Logs.",
        ),
      ]),
      group(ControlCodes.production_logs_identifier, 'Identificação', 'submenu', [
        view(
          ControlCodes.production_logs_identifier_view,
          "Permite acessar a tela 'Identificação' em Logs.",
        ),
      ]),
    ]),
    group(ControlCodes.production_configuration_profile, 'Perfis de Configuração', 'menu', [
      view(
        ControlCodes.production_configuration_profile_view,
        "Permite acessar a tela 'Perfis de Configuração'.",
      ),
    ]),
    permission(
      ControlCodes.production_shared_data,
      'Dados compartilhados',
      'Coloca o usuário no grupo de dados compartilhados de Produção: ele vê os perfis de configuração e logs criados pela equipe (e o que ele criar fica visível para a equipe). Sem este acesso, o usuário vê apenas os perfis e logs criados por ele mesmo, e o que ele criar não aparece para os demais.',
    ),
  ]),

  group(ControlCodes.commercial, 'Comercial', 'module', [
    group(ControlCodes.commercial_credit_analysis, 'Análise de Crédito', 'menu', [
      view(
        ControlCodes.commercial_credit_analysis_view,
        'Permite consultar e realizar tratativas nas análises de crédito.',
      ),
    ]),
    group(ControlCodes.commercial_accounts, 'Contas', 'menu', [
      view(
        ControlCodes.commercial_accounts_view,
        'Permite consultar contas, aplicar filtros e visualizar os resultados.',
        [
          permission(
            ControlCodes.commercial_accounts_view_lgpd_full,
            'LGPD (total)',
            'Permite visualizar integralmente CPF/CNPJ no resultado da consulta.',
          ),
          permission(
            ControlCodes.commercial_accounts_view_lgpd_partial,
            'LGPD (parcial)',
            'Permite visualizar parcialmente CPF/CNPJ no resultado da consulta.',
          ),
          permission(
            ControlCodes.commercial_accounts_new,
            'Nova Conta',
            "Permite acessar o botão 'Nova Conta'.",
          ),
          permission(
            ControlCodes.commercial_accounts_new_sector,
            'Setores',
            'Permite cadastrar e inativar setores.',
          ),
          permission(
            ControlCodes.commercial_accounts_group_admin,
            'Administrar Grupos',
            "Permite acessar o botão 'Administrar Grupos'.",
          ),
          permission(
            ControlCodes.commercial_accounts_access,
            'Acessar conta',
            "Permite acessar o botão 'Acessar conta' no grid de resultados.",
            [
              permission(
                ControlCodes.commercial_accounts_access_lgpd_full,
                'LGPD (total)',
                'Permite visualizar integralmente os dados pessoais da conta.',
              ),
              permission(
                ControlCodes.commercial_accounts_access_lgpd_partial,
                'LGPD (parcial)',
                'Permite visualizar parcialmente os dados pessoais da conta.',
              ),
              permission(
                ControlCodes.commercial_accounts_access_tab_data,
                'Aba Dados da Conta',
                "Permite acessar a aba 'Dados da Conta'.",
                [
                  permission(
                    ControlCodes.commercial_accounts_access_tab_data_edit,
                    'Atualizar/Editar dados',
                    'Permite atualizar e editar os dados da conta.',
                  ),
                  permission(
                    ControlCodes.commercial_accounts_access_tab_data_contacts,
                    'Cadastrar/Editar/Excluir Contatos',
                    'Permite administrar os contatos da conta.',
                  ),
                  permission(
                    ControlCodes.commercial_accounts_access_tab_data_addresses,
                    'Cadastrar/Editar/Excluir Endereços',
                    'Permite administrar os endereços da conta.',
                  ),
                ],
              ),
              permission(
                ControlCodes.commercial_accounts_access_tab_history,
                'Aba Histórico',
                'Permite acessar o histórico da conta.',
              ),
              permission(
                ControlCodes.commercial_accounts_access_tab_attachments,
                'Aba Anexos',
                'Permite acessar os anexos da conta.',
                [
                  permission(
                    ControlCodes.commercial_accounts_access_tab_attachments_delete,
                    'Excluir Anexos',
                    'Permite excluir anexos da conta.',
                  ),
                ],
              ),
            ],
          ),
        ],
      ),
    ]),
    group(ControlCodes.commercial_salesperson, 'Vendedores', 'menu', [
      view(
        ControlCodes.commercial_salesperson_view,
        'Permite consultar vendedores, suas contas e o histórico.',
        [
          permission(
            ControlCodes.commercial_salesperson_update,
            'Gerenciar vendedores e contas',
            'Permite cadastrar vendedores e adicionar ou remover vínculos com contas.',
          ),
        ],
      ),
    ]),
    group(ControlCodes.commercial_product, 'Produtos', 'menu', [
      view(
        ControlCodes.commercial_product_view,
        'Permite consultar e administrar produtos e categorias.',
      ),
    ]),
    group(ControlCodes.commercial_proposal, 'Propostas', 'menu', [
      view(
        ControlCodes.commercial_proposal_view,
        'Permite consultar propostas, aplicar filtros e visualizar os resultados.',
        [
          permission(
            ControlCodes.commercial_proposal_create,
            'Nova Proposta',
            "Permite acessar o botão 'Nova Proposta'.",
          ),
          permission(
            ControlCodes.commercial_proposal_payment_config,
            'Configuração de Pagamento',
            "Permite acessar o botão 'Configuração de Pagamento'.",
          ),
          permission(
            ControlCodes.commercial_proposal_update,
            'Editar proposta',
            "Permite acessar o botão 'Editar proposta'.",
            [
              permission(
                ControlCodes.commercial_proposal_update_specific_price_create,
                'Add Tabela Específica',
                'Permite adicionar preços específicos do cliente ao carrinho.',
              ),
              permission(
                ControlCodes.commercial_proposal_update_specific_price_update,
                'Editar Tabela Específica',
                'Permite editar preços específicos do cliente.',
              ),
              permission(
                ControlCodes.commercial_proposal_update_specific_price_delete,
                'Excluir Tabela Específica',
                'Permite excluir preços específicos do cliente.',
              ),
              permission(
                ControlCodes.commercial_proposal_update_seller,
                'Editar vendedor',
                'Permite editar o vendedor no resumo da proposta.',
              ),
            ],
          ),
        ],
      ),
    ]),
    group(ControlCodes.commercial_price_table, 'Tabela de preços', 'menu', [
      view(
        ControlCodes.commercial_price_table_view,
        'Permite consultar tabelas de preços, aplicar filtros e visualizar o histórico.',
        [
          permission(
            ControlCodes.commercial_price_table_edit,
            'Editar tabela',
            'Permite editar uma tabela de preços.',
            [
              permission(
                ControlCodes.commercial_price_table_edit_save,
                'Salvar',
                'Permite salvar alterações na tabela de preços.',
              ),
              permission(
                ControlCodes.commercial_price_table_edit_cancel,
                'Cancelar tabela',
                'Permite cancelar a tabela de preços.',
              ),
              permission(
                ControlCodes.commercial_price_table_edit_inactivate,
                'Inativar tabela',
                'Permite inativar a tabela de preços.',
              ),
              permission(
                ControlCodes.commercial_price_table_edit_publish,
                'Publicar',
                'Permite publicar a tabela de preços.',
              ),
            ],
          ),
          permission(
            ControlCodes.commercial_price_table_clone,
            'Clonar tabela',
            'Permite clonar uma tabela de preços.',
          ),
          permission(
            ControlCodes.commercial_price_table_create,
            'Nova tabela',
            'Permite cadastrar uma tabela de preços.',
          ),
          permission(
            ControlCodes.commercial_price_table_pix_key,
            'Chave Pix',
            "Permite acessar o botão 'Chave Pix'.",
          ),
        ],
      ),
    ]),
  ]),

  group(ControlCodes.connectivity, 'Conectividade', 'module', [
    group(ControlCodes.connectivity_lora_activation, 'Ativação LoRaWAN', 'menu', [
      group(ControlCodes.connectivity_lora_activation_bulk_actions, 'Ações em massa', 'submenu', [
        view(
          ControlCodes.connectivity_lora_activation_bulk_actions_view,
          "Permite acessar a tela 'Ações em massa'.",
        ),
      ]),
      group(ControlCodes.connectivity_lora_activation_logs, 'Logs de Ativação', 'submenu', [
        view(
          ControlCodes.connectivity_lora_activation_logs_view,
          "Permite acessar a tela 'Logs de Ativação'.",
        ),
      ]),
    ]),
    group(ControlCodes.connectivity_kora_sync, 'Sincronização KORA', 'menu', [
      view(
        ControlCodes.connectivity_kora_sync_view,
        "Permite acessar a tela 'Sincronização KORA'.",
      ),
    ]),
  ]),

  group(ControlCodes.financial, 'Financeiro', 'module', [
    group(ControlCodes.financial_payment_confirmation, 'Confirmação de pagamento', 'menu', [
      view(
        ControlCodes.financial_payment_confirmation_view,
        'Permite consultar pagamentos e aplicar filtros.',
        [
          permission(
            ControlCodes.financial_payment_confirmation_approve,
            'Aprovar pagamento',
            "Permite acessar o botão 'Aprovar pagamento'.",
          ),
        ],
      ),
    ]),
  ]),
];

export const CONTROLS_TREE = [...controlsTree].sort((left, right) =>
  left.name.localeCompare(right.name, 'pt-BR'),
);
